import React, { useEffect, useRef, useState } from "react";

interface CameraProps {
  onClose: () => void;
  onCapture?: (image: string) => void;
  onDone: (images: string[]) => void;
}

const CameraUpload: React.FC<CameraProps> = ({
  onClose,
  onCapture,
  onDone,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement("canvas");

    const videoAspectRatio = video.videoWidth / video.videoHeight;
    const elementAspectRatio = video.clientWidth / video.clientHeight;

    let drawWidth: number, drawHeight: number, offsetX: number, offsetY: number;

    if (videoAspectRatio > elementAspectRatio) {
      drawHeight = video.videoHeight;
      drawWidth = video.videoHeight * elementAspectRatio;
      offsetX = (video.videoWidth - drawWidth) / 2;
      offsetY = 0;
    } else {
      drawWidth = video.videoWidth;
      drawHeight = video.videoWidth / elementAspectRatio;
      offsetX = 0;
      offsetY = (video.videoHeight - drawHeight) / 2;
    }

    canvas.width = drawWidth;
    canvas.height = drawHeight;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(
        video,
        offsetX,
        offsetY,
        drawWidth,
        drawHeight,
        0,
        0,
        drawWidth,
        drawHeight
      );

      const imageData = canvas.toDataURL("image/jpeg");
      setCapturedImages((prev) => [...prev, imageData]);

      if (onCapture) {
        onCapture(imageData);
      }
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const handleDone = () => {
    onDone(capturedImages);
    stopCamera();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black z-[9999]">
      <div className="relative h-full">
        {/* Camera Preview */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />

        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 px-4 bg-black bg-opacity-70">
          <div className="flex items-end justify-end pt-10 pb-1">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-white text-sm"
            >
              Close
            </button>
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-black bg-opacity-70">
          <div className="flex justify-between items-center">
            <div className="bg-gray-900 rounded-full p-2 text-white text-sm w-8 h-8 flex items-center justify-center">
              {capturedImages.length}
            </div>
            <button
              onClick={handleCapture}
              className="w-12 h-12 rounded-full bg-white border-4 border-gray-300"
            />
            <button
              onClick={handleDone}
              disabled={capturedImages.length === 0}
              className={`px-4 py-2 text-white text-sm ${
                capturedImages.length === 0 ? "opacity-50" : ""
              }`}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraUpload;
